import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { safeFetch } from '../../utils/fetchJson';
import { compactNum, TIME_OPTIONS } from '../../data/wazuhSecurityConstants';
import { KpiGrid, KpiCard, Panel, ChipGroup, Badge } from '../../components/design';
import Icon from '../../components/Icon';

const PIE_HEX = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];
const SEV_HEX = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b', warning: '#f59e0b', low: '#10b981', info: '#6366f1', unknown: '#64748b' };

function ageSeconds(ts) {
    if (!ts) return null;
    const d = new Date(String(ts).replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return null;
    return Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
}

function ageLabel(ts) {
    const s = ageSeconds(ts);
    if (s === null) return 'no data';
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

function StalenessBadge({ label, ts }) {
    const s = ageSeconds(ts);
    let tone = 'gray';
    if (s !== null) {
        if (s < 300) tone = 'green';
        else if (s < 1800) tone = 'amber';
        else tone = 'red';
    }
    return (
        <div className="list-row" style={{ padding: 10, border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>{label}</span>
            <Badge tone={tone} style={{ marginLeft: 'auto' }}>{ageLabel(ts)}</Badge>
        </div>
    );
}

export default function DashboardIngestion() {
    const [hours, setHours] = useState(24);
    const { data: health } = useQuery({ queryKey: ['ing', 'health'], queryFn: () => safeFetch('/api/health'), staleTime: 30000, refetchInterval: 60000, retry: false });
    const { data: serversData } = useQuery({ queryKey: ['ing', 'servers'], queryFn: () => safeFetch('/api/servers'), staleTime: 30000, refetchInterval: 60000, retry: false });
    const { data: incidentsData } = useQuery({ queryKey: ['ing', 'incidents'], queryFn: () => safeFetch('/api/incidents?limit=5'), staleTime: 30000, refetchInterval: 60000, retry: false });
    const { data: ch } = useQuery({ queryKey: ['ing', 'ch'], queryFn: () => safeFetch('/api/v1/clickstat/ch/health'), staleTime: 60000, refetchInterval: 120000, retry: false });
    const { data: pipeline, isLoading } = useQuery({ queryKey: ['ing', 'pipeline', hours], queryFn: () => safeFetch(`/api/v1/clickstat/pipeline?hours=${hours}`), staleTime: 45000, refetchInterval: 120000, retry: false });
    const { data: security } = useQuery({ queryKey: ['ing', 'security', hours], queryFn: () => safeFetch(`/api/v1/clickstat/security?hours=${hours}`), staleTime: 45000, refetchInterval: 120000, retry: false });

    if (isLoading && !pipeline) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;

    const servers = serversData?.servers || [];
    const online = servers.filter(s => s.status === 'online').length;
    const deadComp = health?.dead_count ?? 0;
    const openInc = incidentsData?.stats?.open ?? incidentsData?.stats?.active ?? (incidentsData?.incidents?.length || 0);

    const rawTl = (pipeline?.raw_timeline || []).map(t => ({ ...t, label: String(t.bucket).replace('T', ' ').slice(5, 16) }));
    const canonTl = (pipeline?.canonical_timeline || []).map(t => ({ ...t, label: String(t.bucket).replace('T', ' ').slice(5, 16) }));
    const merged = {};
    rawTl.forEach(r => { merged[r.label] = { label: r.label, raw: r.count, canon: 0 }; });
    canonTl.forEach(c => {
        if (!merged[c.label]) merged[c.label] = { label: c.label, raw: 0 };
        merged[c.label].canon = c.count;
    });
    const tl = Object.values(merged).sort((a, b) => a.label.localeCompare(b.label));

    const sev = security?.severity || [];
    const maxSev = Math.max(1, ...sev.map(s => s.count));
    const critRules = security?.top_rules || [];
    const maxRule = Math.max(1, ...critRules.map(r => r.count));
    const critical = (security?.recent_critical || []).slice(0, 6);
    const sources = pipeline?.sources || [];
    const maxSource = Math.max(1, ...sources.map(s => s.count));
    const alertTl = (security?.alert_timeline || []).map(t => ({ ...t, label: String(t.bucket).replace('T', ' ').slice(5, 16) }));

    return (
        <div>
            <Panel
                title="Platform Overview"
                sub="Unified platform, data, and security overview"
                actions={
                    <ChipGroup
                        options={TIME_OPTIONS.filter(t => t.hours <= 168).map(t => ({ value: t.hours, label: t.label }))}
                        value={hours}
                        onChange={setHours}
                    />
                }
            >
                <KpiGrid>
                    <KpiCard label="Servers" value={servers.length} icon="server" iconBg="rgba(96,165,250,0.10)" iconColor="#60a5fa" sub={`${online} online · ${servers.length - online} offline`} />
                    <KpiCard label="Components" value={health ? Object.keys(health.components || {}).length : '—'} icon="layers" iconBg="rgba(52,211,153,0.10)" iconColor="#34d399" sub={`${deadComp} dead`} />
                    <KpiCard label="Open Incidents" value={openInc ?? '—'} icon="siren" iconBg="rgba(248,113,113,0.10)" iconColor="#f87171" sub="active incidents" />
                    <KpiCard label="DB Queries (1h)" value={ch ? compactNum(ch.queries_1h) : '—'} icon="database" iconBg="rgba(34,211,238,0.10)" iconColor="#22d3ee" sub={ch ? `p95 ${ch.p95_duration_ms}ms` : ''} />
                </KpiGrid>
                <KpiGrid>
                    <KpiCard label="Logs (1h)" value={pipeline ? compactNum(pipeline.raw_1h) : '—'} icon="file-text" iconBg="rgba(139,92,246,0.10)" iconColor="#8b5cf6" sub={`${pipeline ? compactNum(pipeline.raw_total) : 0} in ${hours}h`} />
                    <KpiCard label="Events (1h)" value={pipeline ? compactNum(pipeline.canonical_1h) : '—'} icon="list-checks" iconBg="rgba(52,211,153,0.10)" iconColor="#34d399" sub="canonical events" />
                    <KpiCard label="Alerts (1h)" value={security ? compactNum(security.alert_1h) : '—'} icon="bell" iconBg="rgba(248,113,113,0.10)" iconColor="#f87171" sub={`${security ? compactNum(security.alert_total) : 0} in ${hours}h`} />
                    <KpiCard label="Anomalies (1h)" value={security ? compactNum(security.anomaly_1h) : '—'} icon="activity" iconBg="rgba(251,191,36,0.10)" iconColor="#fbbf24" sub="detector output" />
                </KpiGrid>
                <div className="grid grid-4 section-gap" style={{ marginTop: 8 }}>
                    <StalenessBadge label="Last log write" ts={pipeline?.raw_last_ts} />
                    <StalenessBadge label="Last event write" ts={pipeline?.canonical_last_ts} />
                    <StalenessBadge label="Last alert" ts={security?.alert_last_ts} />
                    <StalenessBadge label="Last anomaly" ts={security?.anomaly_last_ts} />
                </div>
            </Panel>

            <div className="grid grid-2 section-gap">
                <Panel title="Ingestion Rate (logs vs events)">
                    {tl.length === 0 ? <div className="empty">No ingestion in window</div> : (
                        <div className="chart-box">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={tl}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-3)' }} />
                                    <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} />
                                    <Tooltip formatter={(v) => [compactNum(v), 'events']} contentStyle={{ background: 'var(--bg-2)', border: 'none', borderRadius: 8, color: 'var(--text-1)' }} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Area type="monotone" dataKey="raw" name="raw logs" stroke="#6366f1" fill="#6366f1" fillOpacity={0.25} />
                                    <Area type="monotone" dataKey="canon" name="canonical events" stroke="#10b981" fill="#10b981" fillOpacity={0.25} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </Panel>
                <Panel title="Alert Volume">
                    {alertTl.length === 0 ? <div className="empty">No alerts in window</div> : (
                        <div className="chart-box">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={alertTl}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
                                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-3)' }} />
                                    <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} />
                                    <Tooltip formatter={(v) => [compactNum(v), 'alerts']} contentStyle={{ background: 'var(--bg-2)', border: 'none', borderRadius: 8, color: 'var(--text-1)' }} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Area type="monotone" dataKey="total" name="all alerts" stroke="#6366f1" fill="#6366f1" fillOpacity={0.25} />
                                    <Area type="monotone" dataKey="critical" name="critical" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </Panel>
            </div>

            <div className="grid grid-2 section-gap">
                <Panel title="Alert Severity">
                    {sev.length === 0 ? <div className="empty">No alerts</div> : (
                        <div className="space-y-2.5">
                            {sev.map(s => (
                                <div key={s.severity}>
                                    <div className="flex justify-between text-xs mb-1">
                                        <span style={{ color: 'var(--text-2)', textTransform: 'capitalize' }}>{s.severity}</span>
                                        <span className="mono" style={{ color: 'var(--text-3)' }}>{compactNum(s.count)}</span>
                                    </div>
                                    <div className="progress">
                                        <span style={{ width: `${Math.round((s.count / maxSev) * 100)}%`, background: SEV_HEX[s.severity] || '#64748b' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>
                <Panel title="Log Sources">
                    {sources.length === 0 ? <div className="empty">No sources</div> : (
                        <div className="space-y-2.5">
                            {sources.slice(0, 6).map(s => (
                                <div key={s.source}>
                                    <div className="flex justify-between text-xs mb-1">
                                        <span className="mono" style={{ color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.source}</span>
                                        <span className="mono" style={{ color: 'var(--text-3)', flexShrink: 0 }}>{compactNum(s.count)}</span>
                                    </div>
                                    <div className="progress">
                                        <span style={{ width: `${Math.round((s.count / maxSource) * 100)}%`, background: PIE_HEX[0] }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>
            </div>

            <div className="grid grid-2 section-gap">
                <Panel title="Top Critical Rules">
                    {critRules.length === 0 ? <div className="empty">No critical alerts</div> : (
                        <div className="space-y-2.5">
                            {critRules.map(r => (
                                <div key={r.rule}>
                                    <div className="flex justify-between text-xs mb-1">
                                        <span style={{ color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.rule}</span>
                                        <span className="mono" style={{ color: 'var(--text-3)', flexShrink: 0 }}>{compactNum(r.count)}</span>
                                    </div>
                                    <div className="progress">
                                        <span style={{ width: `${Math.round((r.count / maxRule) * 100)}%`, background: '#ef4444' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>
                <Panel title="Recent Critical Alerts" noPad>
                    {critical.length === 0 ? <div className="empty">No critical alerts in window</div> : (
                        <div>
                            {critical.map((e, i) => (
                                <div key={i} className="alert-item" style={{ cursor: 'default' }}>
                                    <div className="alert-icon" style={{ background: 'rgba(248,113,113,0.15)', color: '#f87171' }}>
                                        <Icon name="alert-triangle" size={16} />
                                    </div>
                                    <div className="alert-body">
                                        <div className="alert-title" style={{ fontSize: 12 }}>{e.rule}</div>
                                        <div className="alert-desc mono">{e.agent || 'unknown'} · {String(e.timestamp).replace('T', ' ').slice(5, 16)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>
            </div>
        </div>
    );
}
